import {
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import get from 'lodash/get'
import omit from 'lodash/omit'
import { forwardRef, Fragment, useCallback, useEffect, useState } from 'react'
import { useFieldArray, UseFieldArrayReturn } from 'react-hook-form'
import {
  AvatarUploadField,
  AvatarUploadFieldProps,
  IconButton,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { ConsoleMyProfileEditQuery, Maybe } from '@/clients/utoniq-core/schema'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

import {
  ConsoleSNSLinkEdit,
  ConsoleSNSLinkEditProps,
  Icons,
} from './ConsoleSNSLinkEdit'
import { ConsoleTextLinkEdit } from './ConsoleTextLinkEdit'

export type Props = {
  artistId: string
  user: Maybe<ConsoleMyProfileEditQuery['artist']>
  onSubmitMyProfile: (inputs: MyProfileValues) => Promise<void>
  onClickAvatar: AvatarUploadFieldProps['onClick']
}

export type MyProfileValues = {
  avatarPath: string | null
  displayName: string
  accountId: string
  introduction: string
  sns: {
    type: string
    url: string
  }[]
  textLink: { content: string; uri: string }[]
}

const schema: UseFormProps['schema'] = o => ({
  avatarPath: o
    .string()
    .optional()
    .allow('', null)
    .label(tLabel('consoleMyProfileEdit.avatarPath')),
  displayName: o
    .string()
    .max(50)
    .label(tLabel('consoleMyProfileEdit.displayName')),
  accountId: o
    .string()
    .pattern(new RegExp(/^[a-zA-Z0-9_]*$/))
    .max(20)
    .label(tLabel('consoleMyProfileEdit.accountId')),
  introduction: o
    .string()
    .max(1000)
    .optional()
    .allow('')
    .label(tLabel('consoleMyProfileEdit.introduction')),
  sns: o
    .array()
    .items({
      type: o.string(),
      url: o.string(),
    })
    .optional(),
  textLink: o
    .array()
    .items({
      content: o.string(),
      uri: o.string(),
    })
    .optional(),
})

export const ProfileEditForm: Organism<Props> = ({
  user,
  artistId,
  onSubmitMyProfile,
  onClickAvatar,
}) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  const { showMessage, showError } = useMessage()
  const [avatarUploading, setAvatarUploading] = useState<boolean>(false)
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    register,
    reset,
    setValue,
    control,
    setError,
  } = useForm<MyProfileValues>({
    schema,
    defaultValues,
  })
  const snsFieldArray = useFieldArray({ control, name: 'sns' })
  const textFieldArray = useFieldArray({ control, name: 'textLink' })

  useEffect(() => {
    if (!user) {
      return
    }

    const textLink = user?.textLink?.map(v => {
      return omit(v, ['__typename']) as { content: string; uri: string }
    })

    reset({
      displayName: user.displayName ?? '',
      avatarPath: null,
      introduction: user.introduction ?? '',
      accountId: user.accountId ?? '',
      sns: [
        { type: 'instagram', url: user.sns?.instagram ?? '' },
        { type: 'twitter', url: user.sns?.twitter ?? '' },
        { type: 'tiktok', url: user.sns?.tiktok ?? '' },
        { type: 'facebook', url: user.sns?.facebook ?? '' },
        { type: 'youtube', url: user.sns?.youtube ?? '' },
      ].filter(o => o.url),
      textLink: textLink ?? [],
    })
  }, [user, reset])

  const onSubmit = handleSubmit(
    async values =>
      await onSubmitMyProfile(values)
        .then(() => {
          showMessage(t('submitSuccess'), 'success')
        })
        .catch(error => {
          handleError(error, setError, showError)
        })
  )

  const handleOnClick = useCallback(async () => {
    setAvatarUploading(true)
    const result = await onClickAvatar().finally(() => {
      setAvatarUploading(false)
    })
    if (!result) {
      return
    }
    setValue('avatarPath', result?.tmpPath ?? null, {
      shouldValidate: true,
    })
    return result
  }, [onClickAvatar, setValue])

  return (
    <Form onSubmit={onSubmit} sx={{ px: 5, pt: 5, pb: 6 }}>
      <Stack sx={{ pb: 5 }}>
        <Typography variant="h5">{t('profileImage')}</Typography>
        <AvatarUploadCell
          onClick={handleOnClick}
          sx={{ pt: 5, pb: 2 }}
          avatarProps={{ src: user?.avatar?.smallUri ?? '' }}
          titleProps={{
            color: 'primary.main',
            sx: { pt: 3 },
            variant: 'subtitle1',
          }}
          type="image"
          error={errors?.avatarPath}
          {...register('avatarPath')}
        />
      </Stack>
      <TextInputCell
        textFieldProps={{
          placeholder: t('placeholderName', { maxCharacters: 50 }),
        }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        type="text"
        error={errors?.displayName}
        {...register('displayName')}
      />
      <TextInputCell
        type="text"
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        textFieldProps={{
          placeholder: t('placeholderId', { maxCharacters: 20 }),
        }}
        error={errors?.accountId}
        {...register('accountId')}
      />
      <SNSInputCell {...snsFieldArray} artistId={artistId} />
      <TextLinkInputCell {...textFieldArray} artistId={artistId} />
      <IntroductionInputCell
        inputProps={{
          type: 'textarea',
          ...register('introduction'),
          error: errors?.introduction,
        }}
      />

      <Stack alignItems="center">
        <Button
          loading={isSubmitting || avatarUploading}
          type="submit"
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting || avatarUploading}
        >
          {t('register')}
        </Button>
      </Stack>
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleMyProfileEdit')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type IntroductionInputCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
}
const IntroductionInputCell: Cell<IntroductionInputCellProps> = props => {
  const { t } = useTranslation('consoleMyProfileEdit')
  return (
    <CellFrame>
      <Typography variant="h5">{t('introductionLabel')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
        {t('introductionNote')}
      </Typography>
      <TextInputCell
        textFieldProps={{
          multiline: true,
          placeholder: t('placeholderDescription', { maxCharacters: 1000 }),
          rows: 5,
          InputProps: {
            sx: {
              px: 4,
              py: 3,
            },
          },
        }}
        {...props.inputProps}
      />
    </CellFrame>
  )
}

const AvatarUploadCell = forwardRef<
  HTMLInputElement,
  Omit<AvatarUploadFieldProps, 't'>
>((props, ref) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  return (
    <CellFrame sx={{ pb: 3 }}>
      <AvatarUploadField {...props} ref={ref} t={t} />
    </CellFrame>
  )
})

type SNSInputCellProps = Pick<
  UseFieldArrayReturn<MyProfileValues, 'sns', 'id'>,
  'fields' | 'remove' | 'append'
> & { artistId: string }
const SNSInputCell: Cell<SNSInputCellProps> = ({
  fields,
  artistId,
  append,
  remove,
}) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  const [open, setOpen] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [type, setType] = useState<ConsoleSNSLinkEditProps['type'] | null>(null)

  const items: { title: string; value: ConsoleSNSLinkEditProps['type'] }[] = [
    { title: 'Instagram', value: 'instagram' },
    { title: 'Twitter', value: 'twitter' },
    { title: 'Tiktok', value: 'tiktok' },
    { title: 'Facebook', value: 'facebook' },
    { title: 'Youtube', value: 'youtube' },
  ]

  const handleSelect = (value: ConsoleSNSLinkEditProps['type']) => {
    setType(value)
    setOpen(false)
    setOpenModal(true)
  }

  return (
    <CellFrame sx={{ pb: 8 }}>
      <Stack spacing={1}>
        <Typography variant="h5">{t('snsLinkEditTitle')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('snsLinkEditDesc')}
        </Typography>
      </Stack>
      {fields.map(({ id, type, url }, index) => {
        const Icon = get(Icons, type, Box)
        return (
          <Fragment key={id}>
            <Stack
              flex={1}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minHeight: 52, px: 0 }}
            >
              <Icon fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography
                width="100%"
                variant="caption"
                sx={{ wordBreak: 'break-all' }}
              >
                {url}
              </Typography>
              <IconButton.Trash
                size="xSmall"
                sx={{ bgcolor: 'grey.50' }}
                onClick={() => remove(index)}
              />
            </Stack>
            <Divider />
          </Fragment>
        )
      })}
      {fields.length < 5 && (
        <Button variant="outlined" sx={{ mt: 3 }} onClick={() => setOpen(true)}>
          {t('add')}
        </Button>
      )}
      <Drawer open={open} onClose={() => setOpen(false)}>
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          sx={{ p: 5 }}
        >
          {t('selectSnsType')}
        </Typography>
        <Divider />
        <List>
          {items.map(({ title, value }) => {
            const Icon = Icons[value]
            if (fields.find(item => item.type === value)) {
              return null
            }
            return (
              <ListItemButton
                component={Box}
                key={value}
                sx={{ minHeight: 52 }}
                onClick={() => handleSelect(value)}
              >
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Icon fontSize="small" sx={{ color: 'grey.700' }} />
                  <Typography variant="h6">{title}</Typography>
                </Stack>
              </ListItemButton>
            )
          })}
        </List>
        <Divider />
        <Stack sx={{ py: 3 }} alignItems="center">
          <Button
            variant="text"
            onClick={() => setOpen(false)}
            sx={{ width: 'fit-content' }}
          >
            <Typography align="center" variant="h5" color="primary.main">
              {t('cancel')}
            </Typography>
          </Button>
        </Stack>
      </Drawer>
      {type && (
        <ConsoleSNSLinkEdit
          type={type}
          open={openModal}
          artistId={artistId}
          onClose={() => setOpenModal(false)}
          onSubmit={values => {
            setType(null)
            append(values)
          }}
        />
      )}
    </CellFrame>
  )
}

type TextLinkInputCellProps = Pick<
  UseFieldArrayReturn<MyProfileValues, 'textLink', 'id'>,
  'fields' | 'remove' | 'append'
> & { artistId: string }
const TextLinkInputCell: Cell<TextLinkInputCellProps> = ({
  fields,
  append,
  remove,
  artistId,
}) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  const [open, setOpen] = useState<boolean>(false)

  return (
    <CellFrame sx={{ pb: 8 }}>
      <Stack spacing={1}>
        <Typography variant="h5">{t('textLinkEditTitle')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('textLinkEditDesc')}
        </Typography>
      </Stack>
      {fields.map(({ content, id }, index) => (
        <Fragment key={id}>
          <Stack
            flex={1}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ minHeight: 48, px: 0 }}
          >
            <Typography
              width="100%"
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {content}
            </Typography>
            <IconButton.Trash
              size="xSmall"
              sx={{ bgcolor: 'grey.50' }}
              onClick={() => remove(index)}
            />
          </Stack>
          <Divider />
        </Fragment>
      ))}
      {fields.length < 5 && (
        <Button variant="outlined" sx={{ mt: 3 }} onClick={() => setOpen(true)}>
          {t('add')}
        </Button>
      )}
      <ConsoleTextLinkEdit
        open={open}
        artistId={artistId}
        onClose={() => setOpen(false)}
        onSubmit={values => append(values)}
      />
    </CellFrame>
  )
}

const defaultValues: MyProfileValues = {
  avatarPath: null,
  displayName: '',
  accountId: '',
  introduction: '',
  sns: [],
  textLink: [],
}
